import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  api,
  ApiError,
  type AccessLevel,
  type AdminDepartment,
  type CatalogAdminCategory,
  type CatalogAdminItem,
} from '../api';
import { Alert, Button, Card, ConfirmDialog, Flash, PageHeader, Spinner, Switch } from '../components/ui';
import { IconClipboard } from '../components/icons';

/**
 * The form's contents, editable without a deploy (SPEC §2). Everything on this
 * page feeds the enrolment form: the folders people can ask for, the systems,
 * the hardware, the locations, and the department list.
 *
 * Retiring is the normal way to remove something. Deleting is offered only
 * while nothing has ever requested the item, because signed records point at
 * these rows and a completed enrolment must stay readable years later.
 */
export function AdminSettings() {
  const [tab, setTab] = useState<string>('DEPARTMENTS');
  const [flash, setFlash] = useState<string | null>(null);

  const catalog = useQuery({ queryKey: ['admin', 'catalog'], queryFn: () => api.adminCatalog() });
  const departments = useQuery({ queryKey: ['admin', 'departments'], queryFn: () => api.adminDepartments() });

  if (catalog.isPending || departments.isPending) return <Spinner label="Loading the form settings" />;
  if (catalog.isError) return <Alert>{message(catalog.error, 'Could not load the catalogues.')}</Alert>;
  if (departments.isError) return <Alert>{message(departments.error, 'Could not load the departments.')}</Alert>;

  const tabs = [
    { key: 'DEPARTMENTS', label: 'Departments', count: departments.data.departments.filter((d) => d.isActive).length },
    ...catalog.data.categories.map((category) => ({
      key: category.key,
      label: category.label,
      count: category.items.filter((item) => item.isActive).length,
    })),
  ];

  const current = catalog.data.categories.find((category) => category.key === tab);

  return (
    <div>
      <PageHeader
        title="Form settings"
        description="What people can choose on the enrolment form. Changes here apply to new requests straight away; requests already signed keep exactly what they were signed with."
      />

      <div className="mb-6 flex flex-wrap gap-2">
        {tabs.map((item) => (
          <button
            key={item.key}
            type="button"
            onClick={() => setTab(item.key)}
            aria-current={tab === item.key ? 'true' : undefined}
            className={
              tab === item.key
                ? 'rounded-lg bg-brand-600 px-3 py-1.5 text-sm font-medium text-white'
                : 'rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm text-slate-700 transition-colors hover:border-slate-300 hover:bg-slate-50 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-300 dark:hover:bg-slate-800'
            }
          >
            {item.label}
            <span
              className={`ml-2 tabular-nums ${tab === item.key ? 'text-white/70' : 'text-slate-500 dark:text-slate-400'}`}
            >
              {item.count}
            </span>
          </button>
        ))}
      </div>

      {flash && (
        <div className="mb-6">
          <Flash message={flash} onDismiss={() => setFlash(null)} />
        </div>
      )}

      {tab === 'DEPARTMENTS' ? (
        <Departments departments={departments.data.departments} onDone={setFlash} />
      ) : current ? (
        <Catalogue category={current} onDone={setFlash} />
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Catalogue
// ---------------------------------------------------------------------------

/** An action held back until the administrator confirms it. */
type Pending =
  | { kind: 'retire'; id: string; name: string; usedBy: number }
  | { kind: 'delete'; id: string; name: string };

function Catalogue({ category, onDone }: { category: CatalogAdminCategory; onDone: (message: string) => void }) {
  const queryClient = useQueryClient();
  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['admin', 'catalog'] });
  const [adding, setAdding] = useState(false);
  const [pending, setPending] = useState<Pending | null>(null);

  const create = useMutation({
    mutationFn: (name: string) => api.createCatalogItem({ category: category.key, name }),
    onSuccess: (result) => {
      setAdding(false);
      refresh();
      onDone(`Added “${result.item.name}” to ${category.label}. It is on the form now.`);
    },
  });

  const update = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Parameters<typeof api.updateCatalogItem>[1] }) =>
      api.updateCatalogItem(id, patch),
    onSuccess: (result, variables) => {
      setPending(null);
      refresh();
      if (variables.patch.isActive === false) {
        onDone(`Retired “${result.item.name}”. It is off the form; existing requests are unchanged.`);
      } else if (variables.patch.isActive === true) {
        onDone(`Restored “${result.item.name}”. It is back on the form.`);
      } else if (variables.patch.name !== undefined) {
        onDone(`Renamed to “${result.item.name}”.`);
      } else if (variables.patch.isCompulsory === true) {
        const n = result.appliedTo ?? 0;
        onDone(
          `“${result.item.name}” is now on every enrolment and cannot be unticked.${
            n > 0 ? ` Added to ${n} draft${n === 1 ? '' : 's'} in progress.` : ''
          }`,
        );
      } else if (variables.patch.isCompulsory === false) {
        onDone(`“${result.item.name}” is optional again. Requests that already have it keep it.`);
      } else if (variables.patch.defaultAccessLevel !== undefined) {
        const level = result.item.defaultAccessLevel;
        onDone(
          `Default access for “${result.item.name}” set to ${
            level === 'RW' ? 'read & write' : level === 'R' ? 'read' : 'no default'
          }.`,
        );
      }
    },
  });

  const move = useMutation({
    mutationFn: ({ id, direction }: { id: string; direction: 'up' | 'down' }) => api.moveCatalogItem(id, direction),
    onSuccess: refresh,
  });

  const remove = useMutation({
    mutationFn: ({ id }: { id: string; name: string }) => api.deleteCatalogItem(id),
    onSuccess: (_result, variables) => {
      setPending(null);
      refresh();
      onDone(`Deleted “${variables.name}”.`);
    },
  });

  const live = category.items.filter((item) => item.isActive);
  const retired = category.items.filter((item) => !item.isActive);
  const error = create.error ?? update.error ?? move.error ?? remove.error;

  return (
    <div className="space-y-6">
      {error && <Alert>{message(error, 'That change did not go through.')}</Alert>}

      <Card className="overflow-hidden">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 px-5 py-3 dark:border-slate-800">
          <h2 className="text-sm font-medium text-slate-900 dark:text-white">
            {category.label}
            <span className="ml-2 text-slate-500 dark:text-slate-400">
              {live.length} on the form
              {retired.length > 0 && `, ${retired.length} retired`}
            </span>
          </h2>
          {!adding && (
            <Button size="sm" onClick={() => setAdding(true)}>
              <IconClipboard className="h-4 w-4" />
              Add
            </Button>
          )}
        </div>

        {adding && (
          <AddRow
            placeholder={`New ${category.label.replace(/s$/, '').toLowerCase()}`}
            busy={create.isPending}
            onCancel={() => setAdding(false)}
            onSave={(name) => create.mutate(name)}
          />
        )}

        {live.length === 0 && !adding ? (
          <p className="px-5 py-8 text-center text-sm text-slate-500 dark:text-slate-400">
            Nothing here yet. Anything you add appears on the form immediately.
          </p>
        ) : (
          <ul>
            {live.map((item, index) => (
              <Row
                key={item.id}
                item={item}
                category={category}
                first={index === 0}
                last={index === live.length - 1}
                busy={update.isPending || move.isPending || remove.isPending}
                onRename={(name) => update.mutate({ id: item.id, patch: { name } })}
                onAccessLevel={(level) => update.mutate({ id: item.id, patch: { defaultAccessLevel: level } })}
                onCompulsory={(next) => update.mutate({ id: item.id, patch: { isCompulsory: next } })}
                onMove={(direction) => move.mutate({ id: item.id, direction })}
                onRetire={() => setPending({ kind: 'retire', id: item.id, name: item.name, usedBy: item.usedBy })}
                onDelete={() => setPending({ kind: 'delete', id: item.id, name: item.name })}
              />
            ))}
          </ul>
        )}
      </Card>

      {retired.length > 0 && (
        <Card className="overflow-hidden">
          <div className="border-b border-slate-100 px-5 py-3 dark:border-slate-800">
            <h2 className="text-sm font-medium text-slate-900 dark:text-white">Retired</h2>
            <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
              Hidden from new requests. Existing records still show them.
            </p>
          </div>
          <ul>
            {retired.map((item) => (
              <li
                key={item.id}
                className="flex flex-wrap items-center gap-3 border-b border-slate-100 px-5 py-3 last:border-b-0 dark:border-slate-800/70"
              >
                <span className="text-sm text-slate-500 line-through dark:text-slate-500">{item.name}</span>
                {item.usedBy > 0 && (
                  <span className="text-xs text-slate-500 dark:text-slate-400">
                    on {item.usedBy} request{item.usedBy === 1 ? '' : 's'}
                  </span>
                )}
                <div className="ml-auto flex gap-2">
                  <Button
                    size="sm"
                    variant="secondary"
                    disabled={update.isPending}
                    onClick={() => update.mutate({ id: item.id, patch: { isActive: true } })}
                  >
                    Restore
                  </Button>
                  {item.usedBy === 0 && (
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={remove.isPending}
                      onClick={() => setPending({ kind: 'delete', id: item.id, name: item.name })}
                    >
                      Delete
                    </Button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </Card>
      )}

      {pending?.kind === 'retire' && (
        <ConfirmDialog
          title={`Retire “${pending.name}”?`}
          confirmLabel="Retire"
          tone="primary"
          busy={update.isPending}
          onCancel={() => setPending(null)}
          onConfirm={() => update.mutate({ id: pending.id, patch: { isActive: false } })}
          body={
            <>
              <p>
                It stops appearing on new requests straight away. The{' '}
                {pending.usedBy > 0
                  ? `${pending.usedBy} request${pending.usedBy === 1 ? '' : 's'} that already asked for it keep`
                  : 'requests that already asked for it keep'}{' '}
                it, and signed records are untouched.
              </p>
              <p className="mt-2">You can restore it at any time.</p>
            </>
          }
        />
      )}

      {pending?.kind === 'delete' && (
        <ConfirmDialog
          title={`Delete “${pending.name}”?`}
          confirmLabel="Delete"
          busy={remove.isPending}
          onCancel={() => setPending(null)}
          onConfirm={() => remove.mutate({ id: pending.id, name: pending.name })}
          body={
            <>
              <p>
                Nothing has ever requested this, so deleting it loses nothing, but it is gone for good.
              </p>
              <p className="mt-2">If you might want it back, retire it instead.</p>
            </>
          }
        />
      )}
    </div>
  );
}

function Row({
  item,
  category,
  first,
  last,
  busy,
  onRename,
  onAccessLevel,
  onCompulsory,
  onMove,
  onRetire,
  onDelete,
}: {
  item: CatalogAdminItem;
  category: CatalogAdminCategory;
  first: boolean;
  last: boolean;
  busy: boolean;
  onRename: (name: string) => void;
  onAccessLevel: (level: AccessLevel | null) => void;
  onCompulsory: (next: boolean) => void;
  onMove: (direction: 'up' | 'down') => void;
  onRetire: () => void;
  onDelete: () => void;
}) {
  const [draft, setDraft] = useState(item.name);
  const [editing, setEditing] = useState(false);

  function commit() {
    const next = draft.trim();
    setEditing(false);
    if (next && next !== item.name) onRename(next);
    else setDraft(item.name);
  }

  return (
    <li className="flex flex-wrap items-center gap-3 border-b border-slate-100 px-5 py-2.5 last:border-b-0 hover:bg-slate-50/70 dark:border-slate-800/70 dark:hover:bg-slate-800/40">
      <div className="flex shrink-0 flex-col">
        <button
          type="button"
          aria-label={`Move ${item.name} up`}
          disabled={first || busy}
          onClick={() => onMove('up')}
          className="px-1 text-xs leading-none text-slate-500 hover:text-slate-700 dark:text-slate-400 disabled:opacity-25 dark:hover:text-slate-200"
        >
          ▲
        </button>
        <button
          type="button"
          aria-label={`Move ${item.name} down`}
          disabled={last || busy}
          onClick={() => onMove('down')}
          className="px-1 text-xs leading-none text-slate-500 hover:text-slate-700 dark:text-slate-400 disabled:opacity-25 dark:hover:text-slate-200"
        >
          ▼
        </button>
      </div>

      {category.supportsLegacyNumber && item.legacyNumber !== null && (
        <span className="w-8 shrink-0 text-right text-xs tabular-nums text-slate-500 dark:text-slate-400">
          {item.legacyNumber}
        </span>
      )}

      {editing ? (
        <input
          autoFocus
          value={draft}
          disabled={busy}
          onChange={(event) => setDraft(event.target.value)}
          onBlur={commit}
          onKeyDown={(event) => {
            if (event.key === 'Enter') commit();
            if (event.key === 'Escape') {
              setDraft(item.name);
              setEditing(false);
            }
          }}
          aria-label={`Rename ${item.name}`}
          className="min-w-0 flex-1 rounded-lg border border-brand-400 bg-white px-2 py-1 text-sm text-slate-900 dark:bg-slate-900 dark:text-white"
        />
      ) : (
        <button
          type="button"
          onClick={() => setEditing(true)}
          className="min-w-0 flex-1 rounded px-2 py-1 text-left text-sm text-slate-900 hover:bg-slate-100 dark:text-slate-100 dark:hover:bg-slate-800"
          title="Click to rename"
        >
          {item.name}
        </button>
      )}

      {category.supportsAccessLevel && (
        <select
          aria-label={`Default access for ${item.name}`}
          value={item.defaultAccessLevel ?? ''}
          disabled={busy}
          onChange={(event) => onAccessLevel((event.target.value || null) as AccessLevel | null)}
          className="w-32 shrink-0 rounded-lg border border-slate-300 bg-white px-2 py-1 text-xs text-slate-700 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300"
        >
          <option value="">No default</option>
          <option value="R">Read</option>
          <option value="RW">Read & write</option>
        </select>
      )}

      {/* Compulsory: on every enrolment, and nobody can untick it. */}
      <label className="flex shrink-0 items-center gap-2 text-xs text-slate-600 dark:text-slate-300">
        <Switch
          id={`compulsory-${item.id}`}
          checked={item.isCompulsory}
          disabled={busy}
          onChange={onCompulsory}
          label={`${item.name} is compulsory for everyone`}
        />
        <span>Compulsory</span>
      </label>

      <span className="w-24 shrink-0 text-right text-xs text-slate-500 dark:text-slate-400">
        {item.usedBy > 0 ? `${item.usedBy} request${item.usedBy === 1 ? '' : 's'}` : 'never used'}
      </span>

      {/* Fixed width: a row that cannot be deleted must not shuffle the others. */}
      <div className="flex w-40 shrink-0 justify-end gap-1">
        <Button size="sm" variant="ghost" disabled={busy} onClick={onRetire}>
          Retire
        </Button>
        {item.usedBy === 0 && (
          <Button size="sm" variant="ghost" disabled={busy} onClick={onDelete}>
            Delete
          </Button>
        )}
      </div>
    </li>
  );
}

// ---------------------------------------------------------------------------
// Departments
// ---------------------------------------------------------------------------

function Departments({
  departments,
  onDone,
}: {
  departments: AdminDepartment[];
  onDone: (message: string) => void;
}) {
  const queryClient = useQueryClient();
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['admin', 'departments'] });
    void queryClient.invalidateQueries({ queryKey: ['departments'] });
  };
  const [adding, setAdding] = useState(false);
  const [pending, setPending] = useState<Pending | null>(null);

  const create = useMutation({
    mutationFn: (name: string) => api.createDepartment(name, null),
    onSuccess: (result) => {
      setAdding(false);
      refresh();
      onDone(`Added “${result.department.name}”. It is on the form now.`);
    },
  });
  const update = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Parameters<typeof api.updateDepartment>[1] }) =>
      api.updateDepartment(id, patch),
    onSuccess: (result, variables) => {
      setPending(null);
      refresh();
      if (variables.patch.isActive === false) {
        onDone(`Retired “${result.department.name}”. It is off the form; existing records are unchanged.`);
      } else if (variables.patch.isActive === true) {
        onDone(`Restored “${result.department.name}”. It is back on the form.`);
      } else if (variables.patch.name !== undefined) {
        onDone(`Renamed to “${result.department.name}”.`);
      }
    },
  });
  const remove = useMutation({
    mutationFn: ({ id }: { id: string; name: string }) => api.deleteDepartment(id),
    onSuccess: (_result, variables) => {
      setPending(null);
      refresh();
      onDone(`Deleted “${variables.name}”.`);
    },
  });

  const live = departments.filter((department) => department.isActive);
  const retired = departments.filter((department) => !department.isActive);
  const error = create.error ?? update.error ?? remove.error;

  return (
    <div className="space-y-6">
      {error && <Alert>{message(error, 'That change did not go through.')}</Alert>}

      <Alert tone="info">
        The list below is the placeholder set I seeded from the paper form. Replace it with NHC's real
        departments: rename what is close, retire what does not exist, and add what is missing.
      </Alert>

      <Card className="overflow-hidden">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 px-5 py-3 dark:border-slate-800">
          <h2 className="text-sm font-medium text-slate-900 dark:text-white">
            Departments
            <span className="ml-2 text-slate-500 dark:text-slate-400">{live.length} on the form</span>
          </h2>
          {!adding && (
            <Button size="sm" onClick={() => setAdding(true)}>
              <IconClipboard className="h-4 w-4" />
              Add
            </Button>
          )}
        </div>

        {adding && (
          <AddRow
            placeholder="New department"
            busy={create.isPending}
            onCancel={() => setAdding(false)}
            onSave={(name) => create.mutate(name)}
          />
        )}

        <ul>
          {live.map((department) => (
            <DepartmentRow
              key={department.id}
              department={department}
              busy={update.isPending || remove.isPending}
              onRename={(name) => update.mutate({ id: department.id, patch: { name } })}
              onRetire={() =>
                setPending({
                  kind: 'retire',
                  id: department.id,
                  name: department.name,
                  usedBy: department.usedBy + department.userCount,
                })
              }
              onDelete={() => setPending({ kind: 'delete', id: department.id, name: department.name })}
            />
          ))}
        </ul>
      </Card>

      {retired.length > 0 && (
        <Card className="overflow-hidden">
          <div className="border-b border-slate-100 px-5 py-3 dark:border-slate-800">
            <h2 className="text-sm font-medium text-slate-900 dark:text-white">Retired</h2>
          </div>
          <ul>
            {retired.map((department) => (
              <li
                key={department.id}
                className="flex flex-wrap items-center gap-3 border-b border-slate-100 px-5 py-3 last:border-b-0 dark:border-slate-800/70"
              >
                <span className="text-sm text-slate-500 line-through">{department.name}</span>
                <div className="ml-auto flex gap-2">
                  <Button
                    size="sm"
                    variant="secondary"
                    disabled={update.isPending}
                    onClick={() => update.mutate({ id: department.id, patch: { isActive: true } })}
                  >
                    Restore
                  </Button>
                  {department.usedBy === 0 && department.userCount === 0 && (
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={remove.isPending}
                      onClick={() => setPending({ kind: 'delete', id: department.id, name: department.name })}
                    >
                      Delete
                    </Button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </Card>
      )}

      {pending?.kind === 'retire' && (
        <ConfirmDialog
          title={`Retire “${pending.name}”?`}
          confirmLabel="Retire"
          tone="primary"
          busy={update.isPending}
          onCancel={() => setPending(null)}
          onConfirm={() => update.mutate({ id: pending.id, patch: { isActive: false } })}
          body={
            <>
              <p>
                Nobody will be able to pick it on a new request.{' '}
                {pending.usedBy > 0
                  ? `The ${pending.usedBy} user${pending.usedBy === 1 ? '' : 's'} and request${pending.usedBy === 1 ? '' : 's'} already attached to it keep it.`
                  : 'Nothing is attached to it.'}
              </p>
              <p className="mt-2">You can restore it at any time.</p>
            </>
          }
        />
      )}

      {pending?.kind === 'delete' && (
        <ConfirmDialog
          title={`Delete “${pending.name}”?`}
          confirmLabel="Delete"
          busy={remove.isPending}
          onCancel={() => setPending(null)}
          onConfirm={() => remove.mutate({ id: pending.id, name: pending.name })}
          body={
            <>
              <p>No users or requests are attached to it, so deleting it loses nothing, but it is gone for good.</p>
              <p className="mt-2">If you might want it back, retire it instead.</p>
            </>
          }
        />
      )}
    </div>
  );
}

function DepartmentRow({
  department,
  busy,
  onRename,
  onRetire,
  onDelete,
}: {
  department: AdminDepartment;
  busy: boolean;
  onRename: (name: string) => void;
  onRetire: () => void;
  onDelete: () => void;
}) {
  const [draft, setDraft] = useState(department.name);
  const [editing, setEditing] = useState(false);
  const attached = department.usedBy + department.userCount;

  function commit() {
    const next = draft.trim();
    setEditing(false);
    if (next && next !== department.name) onRename(next);
    else setDraft(department.name);
  }

  return (
    <li className="flex flex-wrap items-center gap-3 border-b border-slate-100 px-5 py-2.5 last:border-b-0 hover:bg-slate-50/70 dark:border-slate-800/70 dark:hover:bg-slate-800/40">
      {editing ? (
        <input
          autoFocus
          value={draft}
          disabled={busy}
          onChange={(event) => setDraft(event.target.value)}
          onBlur={commit}
          onKeyDown={(event) => {
            if (event.key === 'Enter') commit();
            if (event.key === 'Escape') {
              setDraft(department.name);
              setEditing(false);
            }
          }}
          aria-label={`Rename ${department.name}`}
          className="min-w-0 flex-1 rounded-lg border border-brand-400 bg-white px-2 py-1 text-sm text-slate-900 dark:bg-slate-900 dark:text-white"
        />
      ) : (
        <button
          type="button"
          onClick={() => setEditing(true)}
          className="min-w-0 flex-1 rounded px-2 py-1 text-left text-sm text-slate-900 hover:bg-slate-100 dark:text-slate-100 dark:hover:bg-slate-800"
          title="Click to rename"
        >
          {department.name}
        </button>
      )}

      <span className="w-32 shrink-0 text-right text-xs text-slate-500 dark:text-slate-400">
        {department.userCount > 0 && `${department.userCount} user${department.userCount === 1 ? '' : 's'}`}
        {department.userCount > 0 && department.usedBy > 0 && ' · '}
        {department.usedBy > 0 && `${department.usedBy} request${department.usedBy === 1 ? '' : 's'}`}
        {attached === 0 && 'never used'}
      </span>

      <div className="flex w-40 shrink-0 justify-end gap-1">
        <Button size="sm" variant="ghost" disabled={busy} onClick={onRetire}>
          Retire
        </Button>
        {attached === 0 && (
          <Button size="sm" variant="ghost" disabled={busy} onClick={onDelete}>
            Delete
          </Button>
        )}
      </div>
    </li>
  );
}

// ---------------------------------------------------------------------------

function AddRow({
  placeholder,
  busy,
  onSave,
  onCancel,
}: {
  placeholder: string;
  busy: boolean;
  onSave: (name: string) => void;
  onCancel: () => void;
}) {
  const [value, setValue] = useState('');

  return (
    <div className="flex flex-wrap items-center gap-2 border-b border-slate-100 bg-brand-50/40 px-5 py-3 dark:border-slate-800 dark:bg-brand-950/20">
      <input
        autoFocus
        value={value}
        disabled={busy}
        placeholder={placeholder}
        onChange={(event) => setValue(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' && value.trim()) onSave(value.trim());
          if (event.key === 'Escape') onCancel();
        }}
        aria-label={placeholder}
        className="min-w-0 flex-1 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-900 dark:border-slate-700 dark:bg-slate-900 dark:text-white"
      />
      <Button size="sm" disabled={busy || !value.trim()} onClick={() => onSave(value.trim())}>
        {busy ? 'Adding…' : 'Add'}
      </Button>
      <Button size="sm" variant="ghost" disabled={busy} onClick={onCancel}>
        Cancel
      </Button>
    </div>
  );
}

function message(error: unknown, fallback: string): string {
  return error instanceof ApiError ? error.message : fallback;
}
